import type { ReactNode } from "react";
import { css } from "@strust/ui/css";

export interface AppFrameProps {
  /** The side menu; it spans the window height and stays in view. */
  readonly sidebar: ReactNode;
  /** The top bar (64 px); it stays above the scrolling region. */
  readonly topbar: ReactNode;
  /** Optional strip under the top bar (notices); not part of the scrolling region. */
  readonly notice?: ReactNode;
  readonly children: ReactNode;
  /** Id of the scrolling region (target of the skip link). */
  readonly regionId?: string;
}

const FRAME = css({
  display: "grid",
  gridTemplateColumns: "auto minmax(0, 1fr)",
  height: "100dvh",
  overflow: "hidden",
  bg: "bg",
  "@media print": { display: "block", height: "auto", overflow: "visible" },
});
const SIDE = css({
  position: "sticky",
  top: "0",
  height: "100dvh",
  minHeight: "0",
  "@media print": { display: "none" },
});
const COLUMN = css({
  display: "flex",
  flexDirection: "column",
  minWidth: "0",
  minHeight: "0",
  height: "100dvh",
  "@media print": { display: "block", height: "auto" },
});
const BAR = css({
  position: "sticky",
  top: "0",
  zIndex: "sticky",
  flexShrink: "0",
  "@media print": { display: "none" },
});
const REGION = css({
  // contains absolutely positioned descendants (visually hidden text, popovers) so they never grow the document
  position: "relative",
  flex: "1",
  minWidth: "0",
  minHeight: "0",
  overflow: "auto",
  _focusVisible: { outline: "none" },
  "@media print": { overflow: "visible" },
});

/**
 * The authenticated app frame (Steel Zero Trust): a `100dvh` grid where the side menu and the top
 * bar never scroll away and only the main region scrolls, so the menu background always spans the
 * window however long the page is (pen.dev shell frame `ccqrM`). The region has no tab stop of its
 * own: every screen holds focusable content, and focus inside it scrolls it from the keyboard.
 */
export function AppFrame({ sidebar, topbar, notice, children, regionId }: AppFrameProps) {
  return (
    <div className={FRAME} data-app-frame="">
      <div className={SIDE} data-app-frame-side="">
        {sidebar}
      </div>
      <div className={COLUMN}>
        <div className={BAR}>
          {topbar}
          {notice}
        </div>
        <div className={REGION} id={regionId} data-app-frame-region="">
          {children}
        </div>
      </div>
    </div>
  );
}
